import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { isSupabaseError, SupabaseCodeError } from '../../../../constants/supabaseErrors';
import type { TopicDatabase } from '../../../../types';
import { usePostTopic } from '../../api/usePostTopic';
import { useUpdateTopic } from '../../api/useUpdateTopic';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Ce champ est obligatoire'),
});
type FormValues = z.infer<typeof formSchema>;

type Props = {
  topic?: TopicDatabase;
  onClose: () => void;
  onCreated?: (topic: TopicDatabase) => void;
};

export function useCreateUpdateTopicModal(props: Props) {
  const postTopic = usePostTopic();
  const updateTopic = useUpdateTopic();
  const [serverError, setServerError] = useState<string | null>(null);
  const isPending = postTopic.isPending || updateTopic.isPending;

  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      name: props.topic?.name ?? '',
    },
    resolver: zodResolver(formSchema),
  });

  async function handleConfirm(data: FormValues) {
    setServerError(null);

    try {
      if (props.topic) {
        await updateTopic.mutateAsync({ id: props.topic.id, command: { name: data.name } });
      } else {
        const created = await postTopic.mutateAsync({ name: data.name });
        props.onCreated?.(created);
      }
      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.code === SupabaseCodeError.UniquenessViolation
          ? 'Un sujet porte déjà ce nom.'
          : error.message);
        return;
      }

      setServerError(props.topic
        ? "L'enregistrement du sujet a échoué. Réessayez."
        : 'La création du sujet a échoué. Réessayez.');
    }
  }

  function handleClose() {
    if (!isPending) {
      props.onClose();
    }
  }

  return {
    form,
    isPending,
    serverError,
    onSubmit: form.handleSubmit(handleConfirm),
    handleClose,
  };
}
